/**
 * Subscription Renewal Page
 * Shows current subscription plus all plans for the module (renew, upgrade, or downgrade).
 * Entry: /subscription/renew?subscriptionId=X or /subscription/renew?module=task|document
 * Layout matches UserSubscription page.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Spinner, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from '../../services/AxiosInstance';
import { clearSubscriptionCache as clearFeatureCache } from '../../utils/featureLock';
import { getActiveSubscriptions, clearSubscriptionCache } from '../../utils/subscriptionCache';
import { sortSubscriptionPlansBySortOrder } from '../../utils/subscriptionPlansSort';
import { formatFlowdoPlanWithModule } from '../../utils/subscriptionPlanLabels';
import { planMatchesModuleTab, subscriptionMatchesModuleTab, subscriptionPlansApiUrl, normalizeModuleTabKey } from '../../utils/subscriptionModuleTabs';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { FiArrowLeft } from 'react-icons/fi';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import './UserSubscription.css';
import './SubscriptionCheckout.css';
import './SubscriptionRenewal.css';

function getPlanPriceForCycle(plan, cycle) {
  return parseFloat(cycle === 'yearly' ? plan?.yearly_price ?? 0 : plan?.monthly_price ?? 0) || 0;
}

/** Align with UserSubscription /checkout navigation */
function getCheckoutIntent(currentPlan, selectedPlan, billingCycle) {
  if (!selectedPlan) return 'new';
  if (!currentPlan) return 'new';
  if (selectedPlan.id === currentPlan.id) return 'renewal';
  const oldP = getPlanPriceForCycle(currentPlan, billingCycle);
  const newP = getPlanPriceForCycle(selectedPlan, billingCycle);
  if (newP > oldP) return 'upgrade';
  if (newP < oldP) return 'downgrade';
  return 'plan_change';
}

function isFreePlan(plan) {
  return (
    plan &&
    parseFloat(plan.monthly_price || 0) === 0 &&
    parseFloat(plan.yearly_price || 0) === 0
  );
}

const SubscriptionRenewal = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/user-subscription');
    }
  };

  const subscriptionIdParam = searchParams.get('subscriptionId');
  const moduleParam = searchParams.get('module'); // 'task' | 'document'

  const [subscription, setSubscription] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const confirmSectionRef = useRef(null);
  const skipInitialConfirmScrollRef = useRef(true);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getPlanPrice = (plan) => {
    const price = billingCycle === 'yearly' ? (plan.yearly_price ?? 0) : (plan.monthly_price ?? 0);
    return parseFloat(price) || 0;
  };

  const isWithinRenewalPeriod = (sub) => {
    if (!sub?.end_date) return false;
    const endDate = new Date(sub.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    const subType = (sub.subscription_type || '').toLowerCase();
    const renewalDays = subType === 'yearly' ? 10 : 5;
    return daysUntilExpiry >= 0 && daysUntilExpiry <= renewalDays;
  };

  const isExpired = (sub) => {
    if (!sub?.end_date) return false;
    const endDate = new Date(sub.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        skipInitialConfirmScrollRef.current = true;
        setLoading(true);
        setError(null);

        const [subsResponse, plansResponse] = await Promise.all([
          getActiveSubscriptions(true),
          axiosInstance.get(subscriptionPlansApiUrl())
        ]);

        const subs = subsResponse?.subscriptions || [];
        const plans = plansResponse?.data?.data || plansResponse?.data || [];

        let targetSubscription = null;
        if (subscriptionIdParam) {
          targetSubscription = subs.find((s) => String(s.id) === String(subscriptionIdParam));
        }
        if (!targetSubscription && moduleParam) {
          targetSubscription = subs.find((s) =>
            subscriptionMatchesModuleTab(s, moduleParam)
          );
        }
        if (!targetSubscription && subs.length > 0) {
          targetSubscription = subs.find((s) => isWithinRenewalPeriod(s) || isExpired(s)) || subs[0];
        }

        if (!targetSubscription) {
          setError('No active subscription found for renewal.');
          setLoading(false);
          return;
        }

        setSubscription(targetSubscription);

        const planId = targetSubscription.SubscriptionPlan?.id || targetSubscription.subscription_plan_id;
        const moduleId = targetSubscription.SubscriptionPlan?.module_id;
        const moduleName = normalizeModuleTabKey(
          targetSubscription.SubscriptionPlan?.Module?.name ||
          targetSubscription.SubscriptionPlan?.Module?.display_name
        );
        const moduleFromSubscription = targetSubscription.SubscriptionPlan?.Module || null;

        const transformedPlans = sortSubscriptionPlansBySortOrder(
          (Array.isArray(plans) ? plans : [])
            .filter((p) => {
              if (moduleId && p.module_id !== moduleId) return false;
              if (moduleName) return planMatchesModuleTab(p, moduleName);
              return true;
            })
            .map((p) => ({
              id: p.id,
              planId: p.id,
              PlanName: p.display_name || p.name,
              display_name: p.display_name || p.name,
              name: p.name,
              module_id: p.module_id,
              Module: p.Module || (p.module_id === moduleId ? moduleFromSubscription : null),
              monthly_price: p.monthly_price,
              yearly_price: p.yearly_price,
              Desscription: p.description || p.Desscription,
              features: p.features_list || p.features || [],
              whats_included: p.whats_included || [],
              sort_order: p.sort_order ?? 0,
            }))
        );

        const subCycle = (targetSubscription.subscription_type || 'monthly').toLowerCase() === 'yearly' ? 'yearly' : 'monthly';
        setBillingCycle(subCycle);

        const currentPlanRow = transformedPlans.find((p) => p.id === planId);

        setSubscriptionPlans(transformedPlans);
        setSelectedPlan(currentPlanRow || transformedPlans[0] || null);
      } catch (err) {
        console.error('Error fetching renewal data:', err);
        setError('Failed to load subscription data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [subscriptionIdParam, moduleParam]);

  useEffect(() => {
    if (!selectedPlan) return;
    if (skipInitialConfirmScrollRef.current) {
      skipInitialConfirmScrollRef.current = false;
      return;
    }
    const t = setTimeout(() => {
      confirmSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [selectedPlan]);

  const plan = subscription?.SubscriptionPlan;
  const moduleName = (plan?.Module?.name || plan?.Module?.display_name || 'Subscription').toLowerCase();
  const moduleTitle = moduleName.charAt(0).toUpperCase() + moduleName.slice(1);
  const planDisplayName = plan?.display_name || plan?.name || '';
  const chargingLabel =
    (subscription?.subscription_type || 'monthly').charAt(0).toUpperCase() +
    (subscription?.subscription_type || 'monthly').slice(1);
  const isCurrentPlan = (p) => p?.id === plan?.id;

  const currentPlanRowForIntent =
    subscriptionPlans.find((p) => p.id === plan?.id) || null;

  const checkoutIntentForRenewal = getCheckoutIntent(
    currentPlanRowForIntent,
    selectedPlan,
    billingCycle
  );

  const selectedIsCurrentPlan = selectedPlan && isCurrentPlan(selectedPlan);
  const selectedPlanIsFree = isFreePlan(selectedPlan);

  /** Paid renewal of same plan, or any paid plan change. Hide when only on current free plan (nothing to pay). */
  const showConfirmSection =
    selectedPlan && (!selectedIsCurrentPlan || !selectedPlanIsFree);

  const handleProceedToCheckout = () => {
    if (!selectedPlan) return;
    if (selectedPlanIsFree) {
      toast.error('This plan is free. Use Confirm Subscription instead.');
      return;
    }
    navigate(
      `/subscription/checkout?planId=${selectedPlan.id}&billingCycle=${billingCycle}&checkoutIntent=${checkoutIntentForRenewal}`
    );
  };

  /** Free plan change (e.g. downgrade) — same as User Subscription; checkout cannot process $0 plans. */
  const handleSubscribe = async () => {
    if (!selectedPlan) {
      toast.error('Please select a subscription plan');
      return;
    }
    if (!selectedPlanIsFree) {
      toast.error('This plan is not available for free subscription');
      return;
    }
    if (selectedIsCurrentPlan) {
      toast.info('You are already on this plan.');
      return;
    }

    setIsSubscribing(true);
    try {
      const response = await axiosInstance.post('/userSubscription/subscribe', {
        subscription_plan_id: selectedPlan.planId || selectedPlan.id,
      });

      if (response) {
        toast.success('Successfully subscribed to the plan!');
        clearSubscriptionCache();
        clearFeatureCache();
        try {
          const featuresResponse = await axiosInstance.get('/userSubscription/features');
          if (featuresResponse.data?.features) {
            localStorage.setItem(
              'userPlanFeatures',
              JSON.stringify(featuresResponse.data.features)
            );
          }
        } catch (err) {
          console.error('Error refreshing user features after subscription:', err);
        }
        try {
          window.dispatchEvent(new Event('user-subscription-updated'));
        } catch (e) {
          /* ignore */
        }
        navigate('/user-subscription');
      } else {
        toast.error(response.data?.message || 'Failed to subscribe');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      toast.error(err?.response?.data?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="row">
          <div className="col-xl-12">
            <div className="card border-0 shadow-sm main-card">
              <div className="card-body p-0">
                <div className="renewal-loading">
                  <Spinner animation="border" style={{ color: '#C42126' }} />
                  <p className="mt-3">Loading renewal options...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <InternalFooter />
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="subscription-page">
        <div className="row">
          <div className="col-xl-12">
            <button type="button" className="renewal-back-button" onClick={handleBack}>
              <FiArrowLeft style={{ marginRight: '0.5rem' }} />
              Back
            </button>
            <div className="card border-0 shadow-sm main-card">
              <div className="card-body p-4 text-center">
                <i className="fas fa-exclamation-triangle text-warning fa-3x mb-3" />
                <h4>Unable to Load Renewal</h4>
                <p>{error || 'No subscription found.'}</p>
                <button
                  type="button"
                  className="new-user-btn btn mt-3 d-inline-flex align-items-center"
                  onClick={handleBack}
                >
                  <FiArrowLeft className="me-2" /> Back
                </button>
              </div>
            </div>
          </div>
        </div>
        <InternalFooter />
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-12">
              <div className="row">
                <div className="col-xl-12">
                  <button type="button" className="renewal-back-button" onClick={handleBack}>
                    <FiArrowLeft style={{ marginRight: '0.5rem' }} />
                    Back
                  </button>
                  <div className="card border-0 shadow-sm main-card">
                    <div className="card-body p-0">
                      <div className="renewal-manage-container">
                        <header className="renewal-page-intro-block">
                          <p className="renewal-page-description">
                            Your subscription is coming up for renewal. Choose your current plan to renew, select a
                            higher package to upgrade, or a lower one to downgrade.
                          </p>
                        </header>

                        <h2 id="renewal-details-heading" className="renewal-section-subtitle">
                          Your Subscription Details
                        </h2>

                        <div className="renewal-current-summary mb-4">
                          <div className="current-plan-card">
                            <div className="current-plan-header">
                              <h5 className="current-plan-title">{plan?.display_name || plan?.name || 'Active Subscription'}</h5>
                            </div>
                            <div className="current-plan-stats">
                              <div className="current-plan-stat">
                                <div className="stat-icon-wrap">
                                  <i className="fas fa-layer-group"></i>
                                </div>
                                <div className="stat-content">
                                  <span className="stat-label">Module</span>
                                  <span className="stat-value">
                                    {planDisplayName ? `${moduleTitle} (${planDisplayName})` : moduleTitle}
                                  </span>
                                </div>
                              </div>
                              <div className="current-plan-stat-divider"></div>
                              <div className="current-plan-stat">
                                <div className="stat-icon-wrap">
                                  <i className="fas fa-sync-alt"></i>
                                </div>
                                <div className="stat-content">
                                  <span className="stat-label">Charging</span>
                                  <span className="stat-value">{chargingLabel}</span>
                                </div>
                              </div>
                              <div className="current-plan-stat-divider"></div>
                              <div className="current-plan-stat">
                                <div className={`stat-icon-wrap ${isExpired(subscription) ? 'stat-icon-expired' : 'stat-icon-expiry'}`}>
                                  <i className="fas fa-calendar-alt"></i>
                                </div>
                                <div className="stat-content">
                                  <span className="stat-label">{isExpired(subscription) ? 'Expired on' : 'Expiry'}</span>
                                  <span className={`stat-value ${isExpired(subscription) ? 'stat-value-expired' : 'stat-value-expiry'}`}>
                                    {formatDate(subscription.end_date)}{isExpired(subscription) ? ' (Already expired)' : ''}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="modern-subscription-container">
                          <h2 id="renewal-options-heading" className="renewal-section-subtitle">
                            Choose your Option
                          </h2>

                          <div className="billing-toggle-container billing-toggle-spacing mb-4">
                            <div className="billing-toggle">
                              <span className={`toggle-label ${billingCycle === 'monthly' ? 'active' : ''}`}>
                                Monthly
                              </span>
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={billingCycle === 'yearly'}
                                  onChange={(e) => setBillingCycle(e.target.checked ? 'yearly' : 'monthly')}
                                />
                                <span className="toggle-slider"></span>
                              </label>
                              <span className={`toggle-label ${billingCycle === 'yearly' ? 'active' : ''}`}>
                                Yearly
                              </span>
                            </div>
                          </div>

                          <div className="plans-section mb-4">
                            <div className="plans-grid">
                            {subscriptionPlans.map((p) => {
                              const curPx = currentPlanRowForIntent
                                ? getPlanPriceForCycle(currentPlanRowForIntent, billingCycle)
                                : null;
                              const px = getPlanPriceForCycle(p, billingCycle);
                              const tierHigher = curPx != null && px > curPx;
                              const tierLower = curPx != null && px < curPx;
                              let selectLabel = 'Select Plan';
                              let selectIcon = 'fa-arrow-right';
                              if (tierHigher) {
                                selectLabel = 'Upgrade';
                                selectIcon = 'fa-arrow-up';
                              } else if (tierLower) {
                                selectLabel = 'Downgrade';
                                selectIcon = 'fa-arrow-down';
                              }
                              return (
                              <div
                                key={p.id}
                                className={`plan-card ${selectedPlan?.id === p.id ? 'selected parent-selected' : ''} ${isCurrentPlan(p) ? 'active-plan' : ''}`}
                                onClick={() => setSelectedPlan(p)}
                              >
                                {isCurrentPlan(p) && (
                                  <div className="active-badge">
                                    <i className="fas fa-check-circle me-1"></i>
                                    Current Plan
                                  </div>
                                )}
                                <div className="plan-header">
                                  <div className="plan-title">{p.PlanName}</div>
                                </div>
                                <div className="plan-content">
                                  <div className="plan-pricing-section text-center">
                                    <div className="plan-pricing">
                                      <span className="price-amount">
                                        {`$${parseFloat(billingCycle === 'monthly' ? (p.monthly_price ?? 0) : (p.yearly_price ?? 0)).toFixed(2)}`}
                                      </span>
                                      <span className="price-period">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                                    </div>
                                  </div>
                                  {p.Desscription && (
                                    <div className="plan-description-section">
                                      <p className="plan-description">{p.Desscription}</p>
                                    </div>
                                  )}
                                  {(() => {
                                    const safeParse = (value) => {
                                      if (!value) return [];
                                      if (Array.isArray(value)) return value;
                                      if (typeof value === 'string') {
                                        if (value.trim().startsWith('[') || value.trim().startsWith('{')) {
                                          try { return JSON.parse(value); } catch (e) { return [value]; }
                                        }
                                        return value.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
                                      }
                                      return [];
                                    };
                                    const included = (p.whats_included && p.whats_included.length > 0)
                                      ? safeParse(p.whats_included)
                                      : (p.features && p.features.length > 0 ? safeParse(p.features) : []);
                                    return included.length > 0 && (
                                      <div className="plan-features">
                                        <h6 className="features-title text-center">What&apos;s Included:</h6>
                                        <ul className="features-list">
                                          {included.map((item, idx) => {
                                            const text = typeof item === 'object' ? (item.display_name || item.name || JSON.stringify(item)) : item;
                                            return (
                                              <li key={idx} className="feature-item">
                                                <i className="fas fa-check text-danger me-2"></i>
                                                <span className="feature-item-text" style={{ whiteSpace: 'pre-line' }}>{text}</span>
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      </div>
                                    );
                                  })()}
                                </div>

                                <div className="plan-action">
                                  {isCurrentPlan(p) ? (
                                    <button
                                      type="button"
                                      className="current-plan-btn"
                                      disabled
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <i className="fas fa-check me-2" aria-hidden />
                                      Current Plan
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="select-plan-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedPlan(p);
                                      }}
                                    >
                                      <i className={`fas ${selectIcon} me-2`} aria-hidden />
                                      {selectLabel}
                                    </button>
                                  )}
                                </div>
                              </div>
                              );
                            })}
                            </div>
                          </div>
                        </div>

                        {showConfirmSection && (
                          <div ref={confirmSectionRef} className="confirm-section mt-4">
                            <div className="w-100">
                              <div className="subscription-confirm-pricing subscription-confirm-detail-layout text-start mb-4 w-100">
                                <Row className="g-3 subscription-confirm-pricing-cols">
                                  <Col xs={12} md={6} className="subscription-confirm-col-left">
                                    <div className="checkout-detail-rows">
                                      <div className="checkout-detail-row">
                                        <span>Selected plan:</span>
                                        <span className="fw-semibold checkout-detail-value">
                                          {formatFlowdoPlanWithModule(selectedPlan)}
                                        </span>
                                      </div>
                                      <div className="checkout-detail-row">
                                        <span>Period:</span>
                                        <span className="fw-semibold checkout-detail-value">
                                          {billingCycle === 'yearly' ? '1 year' : '1 month'}
                                        </span>
                                      </div>
                                    </div>
                                  </Col>
                                  <Col xs={12} md={6} className="subscription-confirm-col-right">
                                    <div className="checkout-detail-rows">
                                      <div className="checkout-detail-row">
                                        <span>Plan amount:</span>
                                        <span className="fw-semibold checkout-detail-value">
                                          {`$${getPlanPriceForCycle(selectedPlan, billingCycle).toFixed(2)}`}
                                        </span>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                                <div className="subscription-confirm-total-block w-100">
                                  <Row className="g-3 subscription-confirm-total-row">
                                    <Col xs={12} md={6} className="d-none d-md-block" aria-hidden="true" />
                                    <Col xs={12} md={6}>
                                      <div className="checkout-detail-row subscription-confirm-total-inner">
                                        <span className="fw-bold">Total due today:</span>
                                        <span className="fw-bold checkout-detail-value">
                                          {`$${getPlanPriceForCycle(selectedPlan, billingCycle).toFixed(2)}`}
                                        </span>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              </div>
                              <div className="text-center">
                                {selectedPlanIsFree ? (
                                  <Button
                                    variant="danger"
                                    size="lg"
                                    onClick={handleSubscribe}
                                    disabled={isSubscribing}
                                    className="px-5 py-3 confirm-button"
                                  >
                                    {isSubscribing ? (
                                      <>
                                        <i className="fas fa-spinner fa-spin me-2" />
                                        Processing...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-check me-2" />
                                        Confirm Free Subscription
                                      </>
                                    )}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="danger"
                                    size="lg"
                                    onClick={handleProceedToCheckout}
                                    disabled={!selectedPlan}
                                    className="px-5 py-3 confirm-button"
                                  >
                                    <i className="fas fa-shopping-cart me-2" />
                                    Continue to Checkout
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <InternalFooter />
    </div>
  );
};

export default SubscriptionRenewal;
